import { useCallback } from "react";
import { useGetList, useNotify } from "react-admin";
import { useMutation } from "@tanstack/react-query";

import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { PolicyMonthlyEnrollment } from "@/types/policyMonthlyEnrollment";

/**
 * Data access for per-month policy enrollment (US 5045 / ADR-047).
 *
 * Reads use the data provider's nested-resource getList. Writes are upserts
 * keyed by coverage_month, so we hit the nested endpoint directly via
 * httpClient (create-or-update is resolved server-side by the upsert key) and
 * refetch the list on success.
 */
export const useMonthlyEnrollments = (policyId: string | undefined) => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const resource = `policies/${policyId}/monthly-enrollments`;
  const baseUrl = `${getPartnerAwareApiUrl()}/policies/${policyId}/monthly-enrollments`;

  const {
    data = [],
    isLoading,
    isError,
    refetch,
  } = useGetList<PolicyMonthlyEnrollment>(
    resource,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "coverage_month", order: "ASC" },
    },
    { enabled: !!policyId }
  );

  const { mutateAsync: upsert, isPending: isSaving } = useMutation({
    mutationFn: async (vars: { coverageMonth: string; enrolledLives: number }) => {
      const { json } = await httpClient(`${baseUrl}/${vars.coverageMonth}`, {
        method: "PUT",
        body: JSON.stringify({ enrolled_lives: vars.enrolledLives }),
      });
      return json as PolicyMonthlyEnrollment;
    },
    onSuccess: () => {
      notify("Enrollment saved", { type: "success" });
      void refetch();
    },
    onError: (error) => notifyError(error),
  });

  const { mutateAsync: remove, isPending: isDeleting } = useMutation({
    mutationFn: async (vars: { coverageMonth: string }) => {
      await httpClient(`${baseUrl}/${vars.coverageMonth}`, { method: "DELETE" });
    },
    onSuccess: () => {
      notify("Enrollment cleared", { type: "success" });
      void refetch();
    },
    onError: (error) => notifyError(error),
  });

  const upsertEnrollment = useCallback(
    (coverageMonth: string, enrolledLives: number) => upsert({ coverageMonth, enrolledLives }),
    [upsert]
  );
  const deleteEnrollment = useCallback((coverageMonth: string) => remove({ coverageMonth }), [remove]);

  return {
    enrollments: data,
    isLoading,
    isError,
    isSaving,
    isDeleting,
    refetch,
    upsertEnrollment,
    deleteEnrollment,
  };
};
